import { useTranslate } from '@ur-solutions/react-hooks'
import {
  useCompositeModules,
  useModuleConfigs,
} from '@/modules/companies/CompanySettings/hooks'
import { useFormcreatorMenuShortcuts } from '@/modules/formcreator'
import { useCompanyHasDashboardShortcuts } from '@/modules/handbook'
import { useHandbookDashboardLinks } from '@/modules/handbook/hooks'
import { TerminalCard } from '@/modules/terminal/TerminalCard'
import { UserDocumentRenewalReminderBanner } from '@/modules/header'
import React from 'react'
import { isMobile } from 'react-device-detect'
import styled from 'styled-components'
import { userHasPermissions } from '@/utils/auth'
import { useAdmin, usePermissions, useUser } from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import { DashboardShortcuts } from '.'
import { DashboardChecklists } from './DashboardChecklists'
import { DashboardControl } from './DashboardControl'
import { DashboardDriverCard } from './DashboardDriverCard'
import { DashboardForms } from './DashboardForms'
import { DashboardLinks } from './DashboardLinks'
import { DashboardNotifications } from './DashboardNotifications'
import { DashboardStatistics } from './DashboardStatistics'

interface WrapperProps {
  $showDriverCard: boolean
  $showTerminal: boolean
  $showShortcuts: boolean
  $showForms: boolean
}
const Wrapper = styled.div<WrapperProps>`
  ${props => props.theme.layout.default};

  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(auto, 1fr));
  grid-template-areas:
    'banner banner'
    'notifications notifications'
    ${props => props.$showDriverCard && "'driver-card driver-card'"}
    ${props => props.$showShortcuts && "'shortcuts   shortcuts'"}
    ${props => props.$showShortcuts && "'links links'"}
    ${props => props.$showForms && "'forms forms'"}
    ${props => props.$showTerminal && "'terminal terminal'"}
    'checklist checklist'
    'stats       control';

  h2 {
    margin-top: 0;
    padding-left: 12px;
    font-size: 1.6em;
    color: ${props => props.theme.colors.primary};
  }

  ${props => props.theme.layout.mobile} {
    display: flex;
    flex-direction: column;
    gap: 0;

    width: 100%;

    h2 {
      margin-bottom: 8px;
      font-size: 1.4em;
      font-weight: 600;
    }

    & > * + * {
      margin-top: 38px;
    }
  }
`
interface AreaProps {
  $area: string
  $padRight?: boolean
}
const Area = styled.div<AreaProps>`
  grid-area: ${props => props.$area};
  margin-bottom: 52px;

  ${props => props.$padRight && 'margin-right: 52px;'};

  ${props => props.theme.layout.mobile} {
    width: 100%;
  }
`

const NoModulesWrapper = styled.div`
  ${props => props.theme.layout.default};
`

export const Dashboard: React.FC = () => {
  const translations = useTranslate({
    noActiveModules: 'common.no-active-modules',
  })

  const user = useUser()
  const admin = useAdmin()
  const { hasAnyModule, active } = useModuleConfigs()

  const { links: handbookLinks } = useHandbookDashboardLinks()
  const { links: formLinks } = useFormcreatorMenuShortcuts()
  const { hasPermissions, hasAnyPermissions } = usePermissions()

  const showHandbookLinks =
    userHasPermissions(user, PERMISSIONS.handbook.view.manualentry) &&
    active.handbook

  const showFormLinks =
    userHasPermissions(user, PERMISSIONS.formcreator.view.formtemplate) &&
    active.handbook &&
    formLinks.length > 0

  const showTerminal =
    userHasPermissions(user, PERMISSIONS.terminal.view.terminalentry) &&
    active.terminal

  const showDriverCard =
    userHasPermissions(user, PERMISSIONS.users.add.driver_card) &&
    !user.activeDriverCard &&
    active.activities &&
    !admin

  const showNotifications = hasPermissions(
    [PERMISSIONS.users.add.driver_card, PERMISSIONS.users.change.user],
    true
  )
  const showUserDocumentBanner = hasAnyPermissions([
    PERMISSIONS.users.change.userdocument,
    PERMISSIONS.users.delete.userdocument,
  ])

  const { dashboardStatisticsModuleActive, activitiesHandbookIssuesActive } =
    useCompositeModules()

  const showControl = activitiesHandbookIssuesActive
  const showStats = dashboardStatisticsModuleActive

  const hasHandbookShortcuts = useCompanyHasDashboardShortcuts()

  return (
    <>
      {hasAnyModule ? (
        <Wrapper
          $showShortcuts={hasAnyModule}
          $showDriverCard={showDriverCard}
          $showTerminal={showTerminal}
          $showForms={showFormLinks}
        >
          {showUserDocumentBanner && (
            <Area $area="banner">
              <UserDocumentRenewalReminderBanner />
            </Area>
          )}

          {showNotifications && (
            <Area $area="notifications">
              <DashboardNotifications />
            </Area>
          )}

          {showDriverCard && (
            <Area $area="driver-card">
              <DashboardDriverCard />
            </Area>
          )}

          {showControl && (
            <Area $area="control">
              <DashboardControl />
            </Area>
          )}

          {hasAnyModule && (
            <Area $area="shortcuts">
              <DashboardShortcuts />
            </Area>
          )}

          {showHandbookLinks && hasHandbookShortcuts && (
            <Area $area="links">
              <DashboardLinks links={handbookLinks} />
            </Area>
          )}

          {showFormLinks && (
            <Area $area="forms">
              <DashboardForms links={formLinks} />
            </Area>
          )}

          {showTerminal && (
            <Area $area="terminal">
              <TerminalCard />
            </Area>
          )}

          {showStats && (
            <Area $area="stats" $padRight={!isMobile}>
              <DashboardStatistics />
            </Area>
          )}

          <Area $area="checklist">
            <DashboardChecklists />
          </Area>
        </Wrapper>
      ) : (
        <NoModulesWrapper>{translations.noActiveModules}</NoModulesWrapper>
      )}
    </>
  )
}
